import { useQuery } from '@tanstack/react-query';

import { objectToURLSearchParams } from '@/helpers/urlParams';
import useImperativeRequestWrapper from '@/hooks/useImperativeRequestWrapper';
import { ExtractFnReturnType, QueryConfig } from '@/lib/react-query';

export interface Carrier {
  name: string;
  tracking_code: string;
}

export interface Position {
  latitude: number;
  longitude: number;
}

export enum ContractState {
  CONTRACT_STATE_UNSPECIFIED = 'UNSPECIFIED',
  CONTRACT_STATE_DRAFT = 'DRAFT',
  CONTRACT_STATE_PURCHASED = 'PURCHASED',
  CONTRACT_STATE_TRANSFERRED = 'TRANSFERRED',
  CONTRACT_STATE_CANCELLED = 'CANCELLED',
  CONTRACT_STATE_DEPLOYED = 'DEPLOYED',
  CONTRACT_STATE_ERROR = 'ERROR',
  CONTRACT_STATE_DELETED = 'DELETED',
}

export interface Shipment {
  carrier: Carrier;
  contractState: ContractState;
  contract_state: ContractState;
  lp_ui: string;
  position: Position;
  shipment_id: string;
  inspection_in_progress?: boolean;
  current_ticket_id?: string;
  current_ticket_start_date?: string;
  last_connection_date?: string;
  box_locked_status?: string;
  last_inspection_status?: string;
  last_inspection_status_date?: string;
  security_code?: string;
}

export interface ShipmentResponse {
  data: Shipment[];
}

export const getShipment = async ({
  criterias,
  makeRequest,
}: {
  criterias: any;
  makeRequest: any;
}): Promise<ShipmentResponse> => {
  const filters = [
    criterias.carrierTrackingCode &&
      `(tracking_code %> '${criterias.carrierTrackingCode}')`,
    criterias.lpui && `(lp_ui %> '${criterias.lpui}')`,
    // required for all calls
    "contract_state = 'DEPLOYED'",
  ]
    .filter(Boolean)
    .join(' AND ');

  const queryParams = {
    limit: criterias.limit || 10,
    'list_request.order_by': 'deployed_at DESC',
    ...(filters && { 'list_request.filter': filters }),
  };

  const { data, error } = await makeRequest({
    path: `/inspection/shipments?${objectToURLSearchParams(queryParams)}`,
  });

  if (error) {
    throw new Error(error);
  }

  return data.shipments;
};

type QueryFnType = typeof getShipment;
type UseGetShipmentOptions = {
  config?: QueryConfig<QueryFnType>;
  criterias?: any;
};

export const useShipment = ({
  criterias,
  config,
}: UseGetShipmentOptions = {}) => {
  const [, makeRequest] = useImperativeRequestWrapper('shipmentV3');

  return useQuery<ExtractFnReturnType<QueryFnType>>({
    ...config,
    queryKey: ['security-check-shipment', criterias],
    queryFn: () => getShipment({ criterias, makeRequest }),
  });
};
